// frontend/src/pages/Contribute.js
import React, { useState } from 'react';
import '../styles/Contribute.css';
import { countries, states } from '../data/locationData'; // Importing from locationData.js

const Contribute = () => {
  const [formData, setFormData] = useState({
    columnOne: '',
    columnTwo: '',
    selectedLaws: '', // Now a single string for the law category
    selectedCountry: '',
    selectedState: '',
  });

  const [isChecked, setIsChecked] = useState(false); // State for checkbox
  const [showSuccessPopup, setShowSuccessPopup] = useState(false); // State for success popup
  
  const lawOptions = [
    'Criminal Law',
    'Civil Law',
    'Labour law',
    'Corporate Law',
    'Family Law',
    'Consumer Law',
  ];

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleCheckboxChange = (e) => {
    setIsChecked(e.target.checked);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.selectedCountry || !formData.selectedState) {
      alert("Please select both Country and State.");
      return;
    }

    try {
      const response = await fetch('/api/contribution/save-contribution', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setShowSuccessPopup(true);
        setFormData({ columnOne: '', columnTwo: '', selectedLaws: '', selectedCountry: '', selectedState: '' });
        setIsChecked(false);
      } else {
        alert('Failed to submit contribution. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting contribution:', error);
      alert('An error occurred while submitting the contribution.');
    }
  };

  return (
    <div className="contribute-container">
      {/* Hero Section - Introduction */}
      <div className="hero-intro-section">
        <div className="hero-content">
          <h1>Share Your Legal Knowledge</h1>
          <p className="hero-subtitle">The contribution system allows users to share their legal knowledge and experiences with the ULJAAPP community to build a comprehensive legal resource database.</p>
          <p className="hero-description">Join thousands of legal professionals, students, and citizens in building the most comprehensive legal knowledge platform</p>
          {/* <div className="hero-stats">
            <div className="stat-item">
              <div className="stat-number">10K+</div>
              <div className="stat-label">Contributors</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">50K+</div>
              <div className="stat-label">Legal Cases</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">1M+</div>
              <div className="stat-label">People Helped</div>
            </div>
          </div> */}
        </div>
      </div>

      <div className="form-section">
        <div className="form-header">
          <h2>Contribution Form</h2>
          <p>Fill out the fields below to share your legal knowledge and experience</p>
        </div>
        
        <form onSubmit={handleSubmit} className="contribute-form">
        {/* Country, State, & Law Category Selection in one row */}
        <div className="dropdown-containerC">
          {/* Country Dropdown */}
          <div className="dropdown-row">
              <label htmlFor="selectedCountry">Country:</label>
              <select
              id="selectedCountry"
              name="selectedCountry"
              value={formData.selectedCountry}
              onChange={handleInputChange}
              required
            >
           
              <option value="">-- Select Country --</option>
              {countries.map((country) => (
                <option key={country} value={country}>
                  {country}
                </option>
              ))}
            </select>
          </div>

          {/* State Dropdown */}
          <div className="dropdown-item">
            <label htmlFor="selectedState">State:</label>
            <select
              id="selectedState"
              name="selectedState"
              value={formData.selectedState}
              onChange={handleInputChange}
              required
              disabled={!formData.selectedCountry}
            >
              <option value="">-- Select State --</option>
              {formData.selectedCountry &&
                states[formData.selectedCountry]?.map((state) => (
                  <option key={state} value={state}>
                    {state}
                  </option>
                ))}
            </select>
          </div>

          {/* Law Category Dropdown */}
          <div className="dropdown-item">
            <label htmlFor="selectedLaws">Law Category:</label>
            <select
              id="selectedLaws"
              name="selectedLaws"
              value={formData.selectedLaws}
              onChange={handleInputChange}
              required
            >
              <option value="">-- Select Law Category --</option>
              {lawOptions.map((law) => (
                <option key={law} value={law}>
                  {law}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Column One */}
        <div className="input-column column-one">
          <label htmlFor="columnOne">Case Heading:</label>
          <textarea
            id="columnOne"
            name="columnOne"
            value={formData.columnOne}
            onChange={handleInputChange}
            placeholder="Type your Case Heading..."
            rows="3"
            required
          />
        </div>

        {/* Column Two */}
        <div className="input-column">
          <label htmlFor="columnTwo">Case summary/Experience:</label>
          <textarea
            id="columnTwo"
            name="columnTwo"
            value={formData.columnTwo}
            onChange={handleInputChange}
            placeholder="Type your Case summary/Experience in detail..."
            rows="5"
            required
          />
        </div>

        {/* Checkbox before submit button */}
        <div className="checkbox-container">
          <input className='checkbox'
            type="checkbox"
            id="confirmContribution"
            checked={isChecked}
            onChange={handleCheckboxChange}
          />
          <label className='confirmContribute' htmlFor="confirmContribution">
          I acknowledge that I am contributing my own knowledge or experience.
          </label>
        </div>

        {/* Submit Button */}
        <div className="sticky-button-container">
          <button
            type="submit"
            className={`submit-button ${isChecked ? 'enabled' : 'disabled'}`}
            disabled={!isChecked}
          >
            Submit Contribution
          </button>
          </div>
        </form>
      </div>

      {/* Professional Information Section */}
      <div className="contribution-info-section">
        <div className="info-header">
          <h2>Why Contribute to ULJAAPP?</h2>
          <p className="info-subtitle">Help build a comprehensive legal resource database for the community</p>
        </div>

        <div className="info-grid">
          <div className="info-card">
            <h3>🎯 Purpose</h3>
            <p>Share your legal knowledge and experiences to create a community-driven platform where real experiences help others navigate the legal system more effectively.</p>
          </div>

          <div className="info-card">
            <h3>👥 Who Can Contribute</h3>
            <ul>
              <li>Legal professionals (lawyers, judges, legal advisors)</li>
              <li>Law students with practical case knowledge</li>
              <li>Citizens with legal case experiences</li>
              <li>Anyone with relevant legal knowledge</li>
            </ul>
          </div>

          <div className="info-card">
            <h3>📝 What to Contribute</h3>
            <ul>
              <li><strong>Case Heading:</strong> Title/summary of a legal case</li>
              <li><strong>Case Summary:</strong> Detailed description including procedures, outcomes, and practical insights</li>
              <li><strong>Jurisdiction:</strong> Country and state-specific legal information</li>
              <li><strong>Category:</strong> Organized by law type for easy discovery</li>
            </ul>
          </div>

          <div className="info-card">
            <h3>⚖️ Why Contribute</h3>
            <ul>
              <li>Help others facing similar legal situations</li>
              <li>Build a crowdsourced legal knowledge repository</li>
              <li>Make legal information more accessible</li>
              <li>Create collaborative legal community</li>
            </ul>
          </div>

          <div className="info-card">
            <h3>🔄 Process</h3>
            <ol>
              <li>Fill the contribution form above</li>
              <li>Submission stored and reviewed by admins</li>
              <li>Approved contributions join searchable database</li>
              <li>Community benefits from shared knowledge</li>
            </ol>
          </div>
        </div>

        <div className="contribution-note">
          <p><strong>📚 Building a Wikipedia for Legal Experiences</strong> - Making legal knowledge practical and accessible for everyone!</p>
        </div>
      </div>

      {/* Success Popup */}
      {showSuccessPopup && (
        <div className="popup-overlay">
          <div className="success-popup">
            <div className="popup-icon">
              <div className="checkmark">✓</div>
            </div>
            <h3>Contribution Submitted Successfully!</h3>
            <p>Thank you for sharing your legal knowledge with the ULJAAPP community.</p>
            <p className="popup-description">
              Your contribution has been received and will be reviewed by our team. 
              Once approved, it will be available in our searchable database to help others.
            </p>
            <button 
              className="popup-close-btn"
              onClick={() => setShowSuccessPopup(false)}
            >
              Continue Contributing
            </button>
          </div>
        </div>
      )}
    </div>
  );
};export default Contribute;